import { DesignerSetting } from '@app/DesignerSetting';
import {
  CommonIntent,
  Design,
  DesignClass,
  DesignIntent,
  DesignItemType,
  GetColumn,
  NodeTypeName,
  SetColumn
} from '@app/types';
import { removeDuplicates } from '../array';
import { createUuid } from '../string';

function makeConfigGetter(
  getter: (config: DesignIntent['props']['config']) => unknown
): (intent: DesignIntent) => unknown {
  return (intent) => {
    return getter(intent.props.config);
  };
}

function makeConfigSetter(
  setter: (
    config: DesignIntent['props']['config'],
    value: string
  ) => void
): (intent: DesignIntent, value: string) => void {
  return (intent, value) => {
    setter(intent.props.config, value);
  };
}

type IntentColumn = (
  GetColumn<DesignIntent, unknown>
  & SetColumn<DesignIntent, string>
);

export type GetIntentColumnsProps = {
  setterData?: {
    /** Used to link up to common intents refrenced in the CSV. */
    commonIntents: CommonIntent[];
  };
  serialized: Design;
};

export function getIntentColumns({
  setterData: {
    commonIntents
  } = {
    commonIntents: []
  },
  serialized
}: GetIntentColumnsProps): IntentColumn[] {
  const commonIntentsMap = Object.fromEntries(
    commonIntents.map((commonIntent) => {
      return [commonIntent.commonId, commonIntent];
    })
  );
  const globalIntents = new Map(
    serialized.globalIntents.map((globalIntent) => {
      return [globalIntent.intent, globalIntent];
    })
  );

  return [
    {
      getter: 'name',
      label: 'Name',
      setter: 'name'
    },
    {
      getter: makeConfigGetter(({ description }) => {
        return description;
      }),
      label: 'Description',
      setter: makeConfigSetter((config, value) => {
        config.description = value;
      })
    },
    {
      getter: makeConfigGetter(({ commonIntent }) => {
        return commonIntent?.intentId;
      }),
      label: 'Library ID',
      setter: makeConfigSetter((config, commonIntentId) => {
        const {
          commonId,
          description,
          name
        } = commonIntentsMap[commonIntentId] ?? {};
        if (!Object.keys(commonIntentsMap ?? {}).length) {
          throw new Error('Library intents not found!');
        } else if (commonIntentId && !commonId) {
          throw new Error(
            `Unable to find library intent with ID '${commonIntentId}'.`
          );
        }

        config.commonIntent = (!commonId) ? null : {
          description: description ?? '',
          intentId: commonId,
          name
        };
      })
    },
    {
      getter: ({ id }) => {
        return globalIntents.get(id)?.moduleProperties.name;
      },
      label: 'Global Module',
      setter: ({ id }, moduleName) => {
        const { timestamp } = window.apm;
        let globalIntent = globalIntents.get(id);
        if (!moduleName) {
          if (globalIntent) {
            globalIntents.delete(id);
            serialized.globalIntents = serialized.globalIntents.filter(({ intent }) => {
              return intent !== id;
            });
          }
          return;
        } else if (!globalIntent) {
          globalIntent = {
            author: DesignerSetting.s.author,
            conditionProperties: {
              _class: DesignClass.condition,
              id: createUuid(),
              props: {
                colorClass: 'primary',
                config: {
                  condition: [],
                  description: '',
                  inputs: [],
                  intent: id,
                  name: '',
                  serializedCondition: ''
                },
                nodeTypeName: NodeTypeName.condition
              }
            },
            createdTime: timestamp,
            id: createUuid(),
            intent: id,
            lastLocalChange: 0,
            lastSavedChange: 0,
            module: '',
            moduleProperties: {
              _class: DesignClass.module,
              id: createUuid(),
              name: '',
              props: {
                config: {
                  description: '',
                  inputs: [],
                  outputs: []
                },
                parentComponentID: ''
              },
              type: DesignItemType.module
            }
          };
          serialized.globalIntents.push(globalIntent);
          globalIntents.set(id, globalIntent);
        }

        const appModule = serialized.components.modules.find(({ name }) => {
          return name === moduleName;
        });
        if (!appModule) {
          throw new Error(`Unable to find module '${moduleName}'.`);
        }
        globalIntent.module = appModule.id;
        globalIntent.moduleProperties.name = appModule.name;
        globalIntent.moduleProperties.props.config.description = appModule.props.config.description;
        globalIntent.moduleProperties.props.parentComponentID = appModule.id;
        globalIntent.lastLocalChange = timestamp;
      }
    },
    {
      getter: makeConfigGetter(({ children }) => {
        return children.map(({ name }) => {
          return name;
        }).join('\n');
      }),
      label: 'Children',
      setter: makeConfigSetter((config, childrenNames) => {
        const intents = new Map(
          serialized.components.intents.map((intent) => {
            return [intent.name, intent];
          })
        );
        config.children = childrenNames.split(/\s+/).filter(Boolean).map((name) => {
          const child = intents.get(name);
          if (!child) {
            throw new Error(`Unable to find intent with name '${name}'.`);
          }
          return {
            customId: child.id,
            description: child.props.config.description,
            name: child.name
          };
        });
      })
    },
    {
      getter: makeConfigGetter(({ parents }) => {
        return parents.map(({ name }) => {
          return name;
        }).join('\n');
      }),
      label: 'Parents',
      setter: makeConfigSetter((config, parentsNames) => {
        const intents = new Map(
          serialized.components.intents.map((intent) => {
            return [intent.name, intent];
          })
        );
        config.parents = parentsNames.split(/\s+/).filter(Boolean).map((name) => {
          const parent = intents.get(name);
          if (!parent) {
            throw new Error(`Unable to find intent with name '${name}'.`);
          }
          return {
            customId: parent.id,
            description: parent.props.config.description,
            name: parent.name
          };
        });
      })
    },
    ...serialized.languages.flatMap(({ language }): IntentColumn[] => {
      return [
        {
          getter: makeConfigGetter(({ localeData }) => {
            return localeData?.[language]?.phrases.join('\n');
          }),
          label: `Utterances (${language})`,
          setter: makeConfigSetter((config, utterances) => {
            config.localeData[language] ??= {
              phrases: [],
              regularExpression: ''
            };
            config.localeData[language]!.phrases = removeDuplicates({
              items: utterances.split(/\s*\n\s*/),
              key: (phrase) => {
                return phrase.toLowerCase();
              }
            });
          })
        },
        {
          getter: makeConfigGetter(({ localeData }) => {
            return localeData?.[language]?.regularExpression;
          }),
          label: `Regular Expression (${language})`,
          setter: makeConfigSetter((config, regularExpression) => {
            config.localeData[language] ??= {
              phrases: [],
              regularExpression: ''
            };
            config.localeData[language]!.regularExpression = regularExpression;
          })
        }
      ];
    })
  ];
}
